import { Dispatch, FC, SetStateAction, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { TbMapPinFilled } from 'react-icons/tb';
import Map, { Marker } from 'react-map-gl/maplibre';
import type { MapRef } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import {
    createThemedMapStyle,
    ThemedMapControls,
    ThemedMapPopup,
} from '@/shared/components/data-visualization/maplibre';
import EmptyView from '@/shared/components/layouts/views/EmptySection';

import { GeoLocation } from '../../types';
import { UsageMapSkeleton } from '../UsageAnalyticsSkeletons';

interface UsageLocationsMapProps {
    geoLocations: GeoLocation[];
    loading?: boolean;
    mapStyle: string;
    popupInfo: GeoLocation | null;
    setPopupInfo: Dispatch<SetStateAction<GeoLocation | null>>;
}

const UsageLocationsMap: FC<UsageLocationsMapProps> = ({
    geoLocations,
    loading = false,
    mapStyle,
    popupInfo,
    setPopupInfo,
}): JSX.Element => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const mapRef = useRef<MapRef | null>(null);
    const resetMapPosition = () => {
        mapRef.current?.flyTo({
            bearing: 0,
            center: [-98, 38],
            duration: 450,
            pitch: 0,
            zoom: 2,
        });
    };

    return (
        <AnalyticsSectionCard
            ariaLabel='Workspace event locations'
            contentStyle={{ padding: '0.75rem' }}
            description='Review the geographic distribution of workspace events for the selected period.'
            icon={<TbMapPinFilled />}
            title='Event Locations'
        >
            {loading ? (
                <UsageMapSkeleton />
            ) : geoLocations.length ? (
                <div className={`cc-themed-map${resolvedThemeName === 'dark' ? ' cc-themed-map--dark' : ''}`}>
                    <Map
                        attributionControl={false}
                        initialViewState={{ longitude: -98, latitude: 38, zoom: 2 }}
                        mapStyle={mapStyle}
                        ref={mapRef}
                        style={createThemedMapStyle(token, {
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            height: 16 * 24,
                            width: '100%',
                        })}
                        mapLib={maplibregl}
                        reuseMaps
                    >
                        <ThemedMapControls mapRef={mapRef} onReset={resetMapPosition} />

                        {geoLocations.map((loc) => (
                            <Marker
                                anchor='center'
                                key={`${loc.longitude}-${loc.latitude}`}
                                latitude={loc.latitude}
                                longitude={loc.longitude}
                                onClick={(event) => {
                                    event.originalEvent.stopPropagation();
                                    setPopupInfo(loc);
                                }}
                            >
                                <TbMapPinFilled size='1rem' style={{ color: token.blue6 }} />
                            </Marker>
                        ))}

                        {popupInfo ? (
                            <ThemedMapPopup
                                icon={<TbMapPinFilled />}
                                items={[{ label: 'Events', value: popupInfo.eventCount.toLocaleString() }]}
                                latitude={popupInfo.latitude}
                                longitude={popupInfo.longitude}
                                onClose={() => setPopupInfo(null)}
                                subtitle={[popupInfo.region, popupInfo.country].filter(Boolean).join(' · ')}
                                title={popupInfo.city}
                            />
                        ) : null}
                    </Map>
                </div>
            ) : (
                <EmptyView
                    description='Event locations will appear here when geographic context is available.'
                    height='15rem'
                    icon={<TbMapPinFilled size='1.25rem' />}
                    label='No Event Locations'
                />
            )}
        </AnalyticsSectionCard>
    );
};

export default UsageLocationsMap;
